import { type AxiosError, useLocale } from '@vivaldis/common'
import {
  useQuery as useReactQuery,
  type UseQueryResult
} from '@tanstack/react-query'
import { useUser } from '@vivaldis/session'
import { createContext, PropsWithChildren, useContext, useMemo } from 'react'
import { START_UP_DOCUMENT_LIST_QUERY_KEY } from '../../screens/StartUpDocuments/constants/queryKeys'
import {
  CnxStartUpDocumentListItem,
  fetchStartUpDocuments,
  FetchStartUpDocumentsQueryParams
} from '../../screens/StartUpDocuments/screens/StartUpDocumentList/api/fetchStartUpDocuments'

type QueryContextValue = UseQueryResult<
  CnxStartUpDocumentListItem[],
  AxiosError
>

const QueryContext = createContext<QueryContextValue | undefined>(undefined)

export function useQuery(): QueryContextValue {
  const context = useContext(QueryContext)

  if (!context) {
    throw new Error('useQuery must be used within a QueryProvider')
  }

  return context
}

export function useIncompletedStartUpDocumentsCount(): number {
  const { data } = useQuery()

  return useMemo(() => {
    return (
      data?.filter(contract => contract.opstartStatus !== 'Completed').length ||
      0
    )
  }, [data])
}

export const StartUpDocumentsBadgesProvider = ({
  children
}: PropsWithChildren<unknown>) => {
  const connexysId = useUser()?.company?.connexysId
  const locale = useLocale()

  const queryVariables: FetchStartUpDocumentsQueryParams = useMemo(() => {
    return {
      companyID: connexysId || 'CONNEXYS_ID_DOES_NOT_EXIST',
      TAAL: locale
    }
  }, [connexysId, locale])

  const queryResult = useReactQuery<CnxStartUpDocumentListItem[], AxiosError>({
    queryKey: [START_UP_DOCUMENT_LIST_QUERY_KEY, queryVariables],
    queryFn: () => fetchStartUpDocuments(queryVariables),
    enabled: !!connexysId
  })

  return (
    <QueryContext.Provider value={queryResult}>
      {children}
    </QueryContext.Provider>
  )
}
